import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { IntlProvider } from 'use-intl';
import type { UserLocale } from '@leanin/shared';
import enMessages from '@/messages/en';
import { loadCatalog, type Catalog } from '@/messages/load';
import { isStaleBundleError } from '@/lib/stale-bundle';

// The app's single locale provider. `AppLocaleProvider` mounts it above the
// router (see app.tsx), so it covers the `_authed` and `_public` trees alike
// and navigating between them cannot flip the language. Signed-in members get
// their stored locale; anonymous visitors get the browser-detected one.
//
// `locale` and `messages` move together in one state object so the tree is
// never rendered with a locale/catalog mismatch: English shows until the
// requested catalog resolves, then both flip atomically.
export function LocaleBoundary({
  locale,
  onStaleBundle,
  children,
}: {
  locale: UserLocale;
  // The guarded stale-bundle reload (stale-bundle.ts).
  onStaleBundle: () => void;
  children: ReactNode;
}) {
  const [active, setActive] = useState<{ locale: UserLocale; messages: Catalog }>({
    locale: 'en',
    messages: enMessages,
  });

  useEffect(() => {
    let cancelled = false;
    loadCatalog(locale)
      .then((messages) => {
        if (!cancelled) setActive({ locale, messages });
      })
      .catch((error: unknown) => {
        // A rejected catalog load is almost always a stale-bundle chunk 404
        // after a deploy: recover with the same guarded reload the app uses
        // for route chunks (handling it here instead of leaning on the global
        // `unhandledrejection` listener, which this `.catch` now pre-empts).
        // Any other failure keeps the English catalog already in state (the
        // deliberate fallback) rather than crashing or stranding the member,
        // logged with the locale so it's diagnosable.
        if (isStaleBundleError(error)) {
          onStaleBundle();
          return;
        }
        console.error('[i18n] catalog load failed; staying on English', {
          locale,
          error,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [locale, onStaleBundle]);

  // Keep `<html lang>` on the catalog actually rendering. `index.html` ships a
  // hardcoded `lang="en"` and nothing else ever updated it, so anyone reading
  // Spanish or French was served English-tagged markup: screen readers apply
  // English pronunciation and hyphenation rules, and `CoachComposer` reads
  // this attribute to choose a speech-recognition language, so dictation ran
  // as en-US whatever the reader had selected.
  //
  // Tracks `active.locale`, not the `locale` prop, so the tag never claims a
  // language whose catalog has not finished loading.
  useEffect(() => {
    const previous = document.documentElement.lang;
    document.documentElement.lang = active.locale;
    return () => {
      document.documentElement.lang = previous;
    };
  }, [active.locale]);

  // Pin `now` at mount so `format.relativeTime` has a stable reference — a
  // fresh `new Date()` per render would let a timestamp drift between renders.
  const now = useMemo(() => new Date(), []);

  // Pin the provider to the viewer's IANA time zone. Without an explicit
  // timeZone, use-intl logs an ENVIRONMENT_FALLBACK error on every
  // `format.dateTime` call (hundreds per render in dev). Viewer-local is the
  // contract everywhere times render (e.g. coach session-meta).
  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  return (
    <IntlProvider
      locale={active.locale}
      messages={active.messages}
      now={now}
      timeZone={timeZone}
    >
      {children}
    </IntlProvider>
  );
}
